import { useState, useMemo } from "react";
import { useAppContext } from "../../../../AppContext";
import { AppDropdownMenu, IDropdownMenuItem } from "../../../../common/components/AppDropdownMenu";
import { HistoryDialog } from "../../../../common/components/history/HistoryDialog";
import { NotesDialog } from "../../../../common/components/common/NotesDialog/NotesDialog";
import { IconType } from "../../../../common/icons/Icon";
import { RenderWhen } from "../../../../common/infrastructure/components/RenderWhen";
import { endpointsMetadata } from "../../../../common/infrastructure/routes/endpointsMetadata";
import { dateUtility } from "../../../../common/infrastructure/utilities/dateUtility";
import { TRANSFER_HISTORY_CONFIG } from "../../../../common/utilities/inventoryTransferAuditLogUtility";
import { IInventoryTransferListItemModel } from "../models/inventoryTransferModels";
import { InventoryTransferStatusBadge } from "./InventoryTransferStatusBadge";
import styles from "./InventoryTransferListItem.module.scss";
import { useApiQuery } from "../../../../common/infrastructure/hooks/useApiQuery";
import { IStudioSettingsModel } from "../../../../common/models/IStudioSettingsModel";

interface IListItemProps {
  transfer: IInventoryTransferListItemModel;
  canEdit: boolean;
  canDelete: boolean;
  onEdit: () => void;
  onToggleDelete: () => void;
}

export const InventoryTransferListItem = ({
  transfer,
  canEdit,
  canDelete,
  onEdit,
  onToggleDelete,
}: IListItemProps) => {
  const { selectedStudioId } = useAppContext();
  const [showHistory, setShowHistory] = useState(false);
  const [showNotes, setShowNotes] = useState(false);

  const { data: studioSettings } = useApiQuery<IStudioSettingsModel>({
    queryKey: [endpointsMetadata.studio.getStudioSettings, selectedStudioId],
    endpoint: endpointsMetadata.studio.getStudioSettings,
    enabled: selectedStudioId > 0,
    globalIsLoading: false,
  });

  const firstFew = transfer.lines.slice(0, 3);
  const extraCount = transfer.lines.length - firstFew.length;

  const menuItems = useMemo<IDropdownMenuItem[]>(() => {
    const items: IDropdownMenuItem[] = [];

    if (canEdit && !transfer.isDeleted) {
      items.push({
        label: "Редактиране",
        icon: IconType.EditIcon,
        onClick: onEdit,
      });
    }

    items.push(
      {
        label: "История",
        icon: IconType.ClockIcon,
        onClick: () => setShowHistory(true),
      },
      {
        label: "Бележки",
        icon: IconType.AddNote,
        onClick: () => setShowNotes(true),
      }
    );

    if (canDelete) {
      items.push(
        { label: "", separator: true },
        {
          label: transfer.isDeleted ? "Възстанови" : "Изтриване",
          icon: transfer.isDeleted ? IconType.PutBackIcon : IconType.DeleteIcon,
          onClick: onToggleDelete,
        }
      );
    }

    return items;
  }, [canEdit, canDelete, transfer.isDeleted, onEdit, onToggleDelete]);

  return (
    <>
      <div
        className={`${styles.row} ${transfer.isDeleted ? styles.row_deleted : ""}`}
      >
        <div className={styles.leading}>
          <div className={styles.numberRow}>
            <span className={styles.number}>{transfer.displayNumber}</span>
            <InventoryTransferStatusBadge
              status={transfer.status}
              isDeleted={transfer.isDeleted}
            />
            <RenderWhen condition={transfer.notesCount > 0}>
              <span className={styles.notesBadge} title="Има бележки">
                📝 {transfer.notesCount}
              </span>
            </RenderWhen>
          </div>
          <div className={styles.dateRow}>
            <span className={styles.date}>
              {dateUtility.formatDate(transfer.deliveryDate)}
            </span>
            <RenderWhen condition={!!transfer.supplier}>
              <span className={styles.supplier}>· {transfer.supplier}</span>
            </RenderWhen>
            <RenderWhen condition={!!transfer.createdByName}>
              <span className={styles.creator}>· {transfer.createdByName}</span>
            </RenderWhen>
          </div>
          <div className={styles.lines}>
            {firstFew.map((line) => (
              <span key={line.id} className={styles.linePill}>
                {line.categoryEmoji ? `${line.categoryEmoji} ` : ""}
                {line.productName}
                <span className={styles.lineQty}> × {line.quantity}</span>
              </span>
            ))}
            <RenderWhen condition={extraCount > 0}>
              <span className={styles.more}>+{extraCount} още</span>
            </RenderWhen>
          </div>
        </div>

        <div className={styles.actions}>
          <AppDropdownMenu
            items={menuItems}
            triggerIconSize="18px"
            triggerIconColor="rgba(255, 255, 255, 0.9)"
          />
        </div>
      </div>

      <HistoryDialog
        entityId={transfer.id}
        entityCreatedOn={transfer.createdOn}
        timezone={studioSettings?.timezone}
        showDialog={showHistory}
        onHide={() => setShowHistory(false)}
        label={`История на ${transfer.displayNumber}`}
        config={TRANSFER_HISTORY_CONFIG}
      />

      <NotesDialog
        entityId={transfer.id}
        showDialog={showNotes}
        onHide={() => setShowNotes(false)}
        label={`Бележки за доставка ${transfer.displayNumber}`}
        getEndpoint={endpointsMetadata.inventoryTransfers.getTransferNotes}
        createEndpoint={endpointsMetadata.inventoryTransfers.addTransferNote}
        updateEndpoint={endpointsMetadata.inventoryTransfers.updateTransferNote}
        deleteEndpoint={endpointsMetadata.inventoryTransfers.deleteTransferNote}
        noResultsLabel="Няма добавени бележки"
        additionalInvalidateKeys={[
          [endpointsMetadata.inventoryTransfers.getAll, selectedStudioId],
          [endpointsMetadata.inventoryTransfers.getAllWithDeleted, selectedStudioId],
        ]}
      />
    </>
  );
};
